import React, { useState } from 'react'
import { ExternalLink, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * Simple Google Drive Setup Instructions
 * Provides step-by-step guide for getting Google Client ID
 */
const GoogleSetupInstructions = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const redirectUri = `${window.location.origin}/auth/callback`

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      toast.success('Copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          )}
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
            First-time setup required (5 minutes)
          </h3>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            To enable Google Drive sync, you need to create a Google Cloud project and get a Client ID. 
            This is free and only takes a few minutes.
          </p>

          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                1
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Go to the{' '}
                  <a
                    href="https://console.cloud.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    <span>Google Cloud Console</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                2
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Create a new project or select an existing one
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                3
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Enable the{' '}
                  <a
                    href="https://console.cloud.google.com/apis/library/drive.googleapis.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    <span>Google Drive API</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                4
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Go to{' '}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    <span>Credentials</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>{' '}
                  and click "Create Credentials" → "OAuth client ID"
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                5
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                  Configure the OAuth consent screen (choose "External" for public use)
                </p>
                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 ml-4">
                  <li>• App name: "NoteSpark.ai" (or your preferred name)</li>
                  <li>• User support email: Your email</li>
                  <li>• Add your email to test users</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                6
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                  Create OAuth 2.0 Client ID with these settings:
                </p>
                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 ml-4">
                  <li>• Application type: "Web application"</li>
                  <li>• Name: "NoteSpark Web Client"</li>
                  <li>• Authorized JavaScript origins: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{window.location.origin}</code></li>
                  <li>• Authorized redirect URIs: 
                    <div className="flex items-center space-x-2 mt-1">
                      <code className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded text-xs">{redirectUri}</code>
                      <button
                        onClick={() => copyToClipboard(redirectUri)}
                        className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Copy redirect URI"
                      >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full mt-0.5">
                7
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Copy the Client ID and add it to your{' '}
                  <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">.env</code> file:
                </p>
                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded border text-xs font-mono text-gray-900 dark:text-gray-100">
                  VITE_GOOGLE_CLIENT_ID=your-client-id-here.apps.googleusercontent.com
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex items-center justify-center w-6 h-6 bg-green-600 dark:bg-green-500 text-white text-xs font-bold rounded-full mt-0.5">
                ✓
              </div>
              <div className="flex-1">
                <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                  That's it! Restart your app and the "Connect" button will work.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> Your app will be in "testing" mode initially, which limits it to 100 users. 
              To publish publicly, you'll need to complete Google's verification process later.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoogleSetupInstructions 